/**
 * CRM 同期（設定 › 同期）。
 *
 * 社内の顧客管理DB（CRM）から一方向 push で届く組織・担当者の反映状況を見る画面。
 * 適用そのものは Webhook → DB の RPC が行い、ここで扱うのは
 * 「自動適用しないと決めたもの」だけ — メールアドレスの変更と大規模な無効化は
 * 保留（sync_holds）になり、ここでの承認が要る。担保はサーバ側にある。
 *
 * 却下は CRM 側を直させるための差し戻しで、CRM のデータが変わらない限り
 * 次のスナップショットで同じ保留が再び作られる（仕様。消したことにならない）。
 */
import { useEffect, useState } from 'react'
import {
  getSyncStatus,
  listSyncEvents,
  listSyncHolds,
  approveSyncHold,
  rejectSyncHold,
  linkOrgExternalRef,
  updateSyncSettings,
  ApiError,
  type SyncStatus,
  type SyncEvent,
  type SyncHold,
} from '../../lib/api.ts'
import { formatDateTime } from '../../lib/format.ts'

/** エラーコード → 日本語。未知のコードは生値のまま出す（調査でそのまま伝えてもらう）。 */
const ERROR_LABEL: Record<string, string> = {
  hold_not_pending: 'この保留はすでに処理済みです（再読み込みすると消えます）',
  user_not_synced: '対象ユーザーが CRM 同期の管理下にありません',
  email_taken: '新しいメールアドレスは別のユーザーが使用しています',
  hold_stale: '保留の作成後にメールが変わっています（次の同期で保留が作り直されます）',
  invalid_hold_data: '保留のデータが壊れています（イベントログを確認してください）',
  already_linked: 'この組織はすでに CRM とひも付いています',
  external_ref_in_use: 'この CRM ID は別の組織で使われています',
  permission_denied: '権限がありません',
}

const describeError = (e: unknown): string => {
  const code = e instanceof ApiError ? e.code : String(e)
  const label = ERROR_LABEL[code]
  return label ? `${label}（${code}）` : `操作に失敗しました（${code}）`
}

const KIND_LABEL: Record<string, string> = {
  email_change: 'メールアドレス変更',
  mass_deactivation: '一括無効化',
}

const OUTCOME_LABEL: Record<string, string> = {
  applied: '適用',
  held: '保留',
  rejected: '拒否',
  duplicate: '重複',
  disabled: '停止中',
  received: '受信',
}

const TYPE_LABEL: Record<string, string> = {
  'company.upserted': '組織の更新',
  'company.deleted': '組織の削除',
  'contact.upserted': '担当者の更新',
  'contact.deleted': '担当者の削除',
  snapshot: '全量スナップショット',
}

/** hold.data の中身は DB 実装に依存するので、期待するキーが無くても壊れないように読む。 */
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const arrLen = (v: unknown): number | null => (Array.isArray(v) ? v.length : null)

const newEmailOf = (h: SyncHold): string | null => str(h.data['new_email']) ?? str(h.data['email'])
const oldEmailOf = (h: SyncHold): string | null => str(h.data['old_email']) ?? h.user?.email ?? null

const massCounts = (h: SyncHold): { users: number | null; orgs: number | null } => ({
  users: arrLen(h.data['users']) ?? arrLen(h.data['user_ids']) ?? arrLen(h.data['deactivate']),
  orgs: arrLen(h.data['orgs']) ?? arrLen(h.data['org_ids']) ?? arrLen(h.data['suspend_orgs']),
})

/** rejected の理由コードを出す。他の outcome は要点が無いので空欄のまま。 */
const eventReason = (ev: SyncEvent): string =>
  str(ev.detail['reason']) ?? ''

export function SyncSettings() {
  const [status, setStatus] = useState<SyncStatus | null>(null)
  const [holds, setHolds] = useState<SyncHold[]>([])
  const [events, setEvents] = useState<SyncEvent[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // 閾値の編集欄。保存までローカルに持ち、読み込みのたびに現在値へ戻す
  const [maxDeact, setMaxDeact] = useState('')
  const [maxPct, setMaxPct] = useState('')
  // 組織のひも付けフォーム
  const [linkOrgId, setLinkOrgId] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [crmUrl, setCrmUrl] = useState('')

  const reload = async () => {
    try {
      const [s, h, ev] = await Promise.all([
        getSyncStatus(),
        listSyncHolds('pending'),
        listSyncEvents(50),
      ])
      setStatus(s.status)
      setHolds(h.holds)
      setEvents(ev.events)
      setMaxDeact(String(s.status.max_deactivations))
      setMaxPct(String(s.status.max_deactivation_pct))
    } catch (e) {
      setError(describeError(e))
    }
  }

  useEffect(() => {
    reload()
  }, [])

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const msg = await fn()
      if (msg) setNotice(msg)
      await reload()
    } catch (e) {
      setError(describeError(e))
    } finally {
      setBusy(false)
    }
  }

  if (!status) {
    return error ? (
      <p className="error small">{error}</p>
    ) : (
      <p className="muted small">読み込み中…</p>
    )
  }

  const toggleEnabled = () => {
    const next = !status.enabled
    if (
      !next &&
      !confirm(
        '同期の受付を停止します。停止中に届いたイベントは記録されますが適用されません。よろしいですか？',
      )
    )
      return
    run(async () => {
      await updateSyncSettings({ enabled: next })
      return next ? '同期を有効にしました' : '同期を停止しました'
    })
  }

  const saveThresholds = () => {
    const deact = Number(maxDeact)
    const pct = Number(maxPct)
    if (!Number.isInteger(deact) || deact < 1) {
      setNotice(null)
      setError('人数の閾値は 1 以上の整数にしてください')
      return
    }
    if (!Number.isInteger(pct) || pct < 1 || pct > 100) {
      setNotice(null)
      setError('割合の閾値は 1〜100 の整数にしてください')
      return
    }
    run(async () => {
      await updateSyncSettings({ maxDeactivations: deact, maxDeactivationPct: pct })
      return '閾値を保存しました'
    })
  }

  const approve = (h: SyncHold) => {
    const message =
      h.kind === 'email_change'
        ? `${h.user?.email ?? '対象ユーザー'} のメールアドレスを「${
            newEmailOf(h) ?? '新しいアドレス'
          }」に変更します。ログインに使うアドレスも変わります。よろしいですか？`
        : '一括無効化を適用します。対象ユーザーは停止され、ログインセッションも失効します。よろしいですか？'
    if (!confirm(message)) return
    run(async () => {
      const { result } = await approveSyncHold(h.id)
      if (result.kind === 'email_change') {
        return `メールアドレスを ${result.old_email ?? '—'} → ${result.new_email ?? '—'} に変更しました`
      }
      if (result.kind === 'mass_deactivation') {
        const orgNote = result.suspended_orgs?.length
          ? `、組織 ${result.suspended_orgs.length} 件を停止しました`
          : ''
        return `${result.deactivated?.length ?? 0} 名を停止しました${orgNote}`
      }
      return '承認しました'
    })
  }

  const reject = (h: SyncHold) => {
    if (
      !confirm(
        'この保留を却下します。CRM 側のデータを直さない限り、次のスナップショットで同じ保留が再び作られます。よろしいですか？',
      )
    )
      return
    run(async () => {
      await rejectSyncHold(h.id)
      return '却下しました'
    })
  }

  const manualOrgs = status.orgs.filter((o) => o.managed_by === 'manual')

  const link = (e: React.FormEvent) => {
    e.preventDefault()
    const org = status.orgs.find((o) => o.id === linkOrgId)
    const ref = externalRef.trim()
    if (!org || !ref) return
    if (
      !confirm(
        `「${org.name}」を CRM（${ref}）とひも付けます。以後この組織は CRM からの同期で更新されます。よろしいですか？`,
      )
    )
      return
    run(async () => {
      await linkOrgExternalRef({
        orgId: org.id,
        externalRef: ref,
        ...(crmUrl.trim() ? { crmUrl: crmUrl.trim() } : {}),
      })
      setLinkOrgId('')
      setExternalRef('')
      setCrmUrl('')
      return `「${org.name}」をひも付けました`
    })
  }

  return (
    <div className="admin">
      <p className="muted small">
        CRM（顧客管理DB）から組織と担当者を一方向で受け取ります。反映は自動ですが、
        <strong>メールアドレスの変更と大規模な無効化は自動適用せず、ここでの承認が必要です。</strong>
      </p>

      {error && <p className="error small">{error}</p>}
      {notice && <p className="success small">{notice}</p>}

      <div className="staff-actions">
        <label className="inline-check">
          <input type="checkbox" checked={status.enabled} disabled={busy} onChange={toggleEnabled} />
          同期を受け付ける
        </label>
        <button className="small" disabled={busy} onClick={() => reload()}>
          再読み込み
        </button>
      </div>

      <dl className="sync-stats">
        <div>
          <dt>最終受信</dt>
          <dd>{formatDateTime(status.last_event_at)}</dd>
        </div>
        <div>
          <dt>24時間の受信 / 拒否</dt>
          <dd>
            {status.events_24h} 件 /{' '}
            {status.rejected_24h > 0 ? (
              <span className="error">{status.rejected_24h} 件</span>
            ) : (
              `${status.rejected_24h} 件`
            )}
          </dd>
        </div>
        <div>
          <dt>保留中の承認</dt>
          <dd>
            {status.pending_holds > 0 ? (
              <span className="warn">{status.pending_holds} 件</span>
            ) : (
              '0 件'
            )}
          </dd>
        </div>
        <div>
          <dt>同期対象</dt>
          <dd>
            組織 {status.synced_orgs} / ユーザー {status.synced_users}
          </dd>
        </div>
      </dl>

      <div className="invite-form">
        <div className="row">
          <div>
            <label htmlFor="sync-max-deact">一括無効化の保留閾値（人数）</label>
            <input
              id="sync-max-deact"
              type="number"
              min={1}
              step={1}
              value={maxDeact}
              onChange={(e) => setMaxDeact(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="sync-max-pct">同（有効な同期ユーザーに対する割合 %）</label>
            <input
              id="sync-max-pct"
              type="number"
              min={1}
              max={100}
              step={1}
              value={maxPct}
              onChange={(e) => setMaxPct(e.target.value)}
            />
          </div>
        </div>
        <p className="muted small">
          全量スナップショットでこれを超える無効化（または組織の停止）が起きる場合は、
          即時適用せず「保留」にして承認を求めます。
        </p>
        <button className="small" disabled={busy} onClick={saveThresholds}>
          閾値を保存
        </button>
      </div>

      <h3 className="small">保留中の承認{holds.length > 0 && `（${holds.length}件）`}</h3>
      {holds.length === 0 ? (
        <p className="muted small">承認待ちの保留はありません。</p>
      ) : (
        <table className="invites">
          <thead>
            <tr>
              <th>種類</th>
              <th>内容</th>
              <th>発生</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {holds.map((h) => {
              const counts = h.kind === 'mass_deactivation' ? massCounts(h) : null
              return (
                <tr key={h.id}>
                  <td className="small">{KIND_LABEL[h.kind] ?? h.kind}</td>
                  <td className="small">
                    {h.kind === 'email_change' ? (
                      <>
                        {h.user ? (h.user.display_name ?? h.user.email) : '（ユーザー不明）'}
                        {h.org && <span className="muted">（{h.org.name}）</span>}
                        <span className="mono small block">
                          {oldEmailOf(h) ?? '—'} → <strong>{newEmailOf(h) ?? '—'}</strong>
                        </span>
                        <span className="muted small block">
                          承認するとログインに使うアドレスも変わります
                        </span>
                      </>
                    ) : (
                      <>
                        {counts?.users != null
                          ? `ユーザー ${counts.users} 名の停止`
                          : '対象は詳細データを確認してください'}
                        {counts?.orgs != null && counts.orgs > 0 && `・組織 ${counts.orgs} 件の停止`}
                        <span className="muted small block">
                          スナップショットの無効化が閾値を超えたため保留されています
                        </span>
                      </>
                    )}
                    <details>
                      <summary className="muted small">詳細データ</summary>
                      <pre className="sync-detail mono">{JSON.stringify(h.data, null, 2)}</pre>
                    </details>
                  </td>
                  <td className="small muted">{formatDateTime(h.created_at)}</td>
                  <td>
                    <div className="account-actions">
                      <button className="small primary" disabled={busy} onClick={() => approve(h)}>
                        承認
                      </button>
                      <button className="small danger" disabled={busy} onClick={() => reject(h)}>
                        却下
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <h3 className="small">組織のひも付け</h3>
      <p className="muted small">
        手動管理の組織を CRM の顧客とひも付けると、以後は CRM からの同期で更新されます
        （名称・状態・担当者。担当者のロールは CRM の管理担当者フラグで決まります）。
      </p>
      <form onSubmit={link} className="invite-form">
        <div className="row">
          <div>
            <label htmlFor="sync-org">組織</label>
            <select id="sync-org" value={linkOrgId} onChange={(e) => setLinkOrgId(e.target.value)}>
              <option value="">選択してください</option>
              {manualOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            {manualOrgs.length === 0 && (
              <p className="muted small">手動管理の組織がありません（すべてひも付け済み）</p>
            )}
          </div>
          <div>
            <label htmlFor="sync-ref">CRM の顧客ID（external_ref）</label>
            <input
              id="sync-ref"
              value={externalRef}
              maxLength={128}
              required
              onChange={(e) => setExternalRef(e.target.value)}
              placeholder="CRM 側の company external_ref"
            />
          </div>
          <div>
            <label htmlFor="sync-url">CRM の顧客ページURL（任意）</label>
            <input
              id="sync-url"
              type="url"
              value={crmUrl}
              onChange={(e) => setCrmUrl(e.target.value)}
              placeholder="https://crm.example.com/companies/…"
            />
          </div>
        </div>
        <button className="primary" disabled={busy || !linkOrgId || !externalRef.trim()}>
          ひも付ける
        </button>
      </form>

      <table className="invites">
        <thead>
          <tr>
            <th>組織</th>
            <th>状態</th>
            <th>管理</th>
            <th>CRM ID</th>
            <th>最終同期</th>
          </tr>
        </thead>
        <tbody>
          {status.orgs.map((o) => (
            <tr key={o.id}>
              <td className="small">
                {o.name}
                {o.crm_url && (
                  <>
                    {' '}
                    <a className="small" href={o.crm_url} target="_blank" rel="noreferrer">
                      CRMで開く
                    </a>
                  </>
                )}
              </td>
              <td className="small">
                {o.status === 'active' ? (
                  <span className="success">有効</span>
                ) : (
                  <span className="error">停止中</span>
                )}
              </td>
              <td className="small">
                {o.managed_by === 'sync' ? 'CRM同期' : <span className="muted">手動</span>}
              </td>
              <td className="mono small">{o.external_ref ?? '—'}</td>
              <td className="small muted">{formatDateTime(o.synced_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {status.orgs.length === 0 && <p className="muted small">組織がまだありません。</p>}

      <h3 className="small">最近のイベント</h3>
      {events.length === 0 ? (
        <p className="muted small">まだイベントを受信していません。</p>
      ) : (
        <table className="invites">
          <thead>
            <tr>
              <th>受信時刻</th>
              <th>種別</th>
              <th>結果</th>
              <th>詳細</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id}>
                <td className="small muted">{formatDateTime(ev.received_at)}</td>
                <td className="small">{TYPE_LABEL[ev.type] ?? ev.type}</td>
                <td>
                  <span className={`sync-outcome ${ev.outcome}`}>
                    {OUTCOME_LABEL[ev.outcome] ?? ev.outcome}
                  </span>
                </td>
                <td className="mono small muted">{eventReason(ev)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
